import { Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { ConvexError } from "convex/values";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { Id } from "~/convex/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Route as OnboardingRoute } from "@/routes/_app/_auth/onboarding/_layout.usertype";
import { Route as PartnershipsRoute } from "@/routes/_app/_auth/dashboard/_layout.partnerships.index";
import { User } from "~/types";
import { api } from "~/convex/_generated/api";
import { useAccount } from "@/state/useAccount";
import { useConvexMutation } from "@convex-dev/react-query";
import { useDebouncedCallback } from "use-debounce";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useToast } from "../ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

interface AppSettingsProps {
  user: User;
  appId: Id<"apps">;
}

export function AppSettings({ user, appId }: AppSettingsProps) {
  const { mutateAsync: updateUserImage } = useMutation({
    mutationFn: useConvexMutation(api.app.updateUserImage),
  });
  const { mutateAsync: removeUserImage } = useMutation({
    mutationFn: useConvexMutation(api.app.removeUserImage),
  });
  const { mutateAsync: updateApp } = useMutation({
    mutationFn: useConvexMutation(api.apps.updateApp),
  });
  const { mutateAsync: deleteApp } = useMutation({
    mutationFn: useConvexMutation(api.apps.deleteApp),
  });
  const {
    setAppId,
    setCreatorId,
    demoModeEnabled,
    setDemoModeEnabled,
    demoAppName,
    demoAppLogo,
    setDemoAppName,
    setDemoAppLogo,
  } = useAccount();
  const { toast } = useToast();
  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const demoLogoFileInputRef = useRef<HTMLInputElement>(null);
  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      await updateUserImage({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        imageId: (uploaded[0].response as any).storageId,
      });
    },
  });
  const { startUpload: startLogoUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = "";
      }
      await updateApp({
        appId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logo: (uploaded[0].response as any).storageId,
      });
    },
  });
  const handleDemoLogoUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setDemoAppLogo(dataUrl);
      toast({
        title: "Demo logo uploaded",
        description: "Your demo logo has been updated successfully",
      });
    };
    reader.onerror = () => {
      toast({
        title: "Upload failed",
        description: "Failed to read the image file. Please try again.",
        variant: "destructive",
      });
    };
    reader.readAsDataURL(file);
  };

  const currentApp = user.appsList.find((app) => app._id === appId);
  const [appDetails, setAppDetails] = useState({
    description: currentApp?.description ?? "",
  });
  const [logoUrl, setLogoUrl] = useState(
    typeof currentApp?.logo === "string" ? currentApp.logo : "",
  );
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const debouncedUpdateApp = useDebouncedCallback(
    async (updates: typeof appDetails) => {
      if (appId) {
        await updateApp({
          appId,
          ...updates,
        });
      }
    },
    1000,
  );

  const handleDeleteApp = async () => {
    try {
      if (appId) {
        await deleteApp({ appId });

        toast({
          title: "App deleted",
          description: "Your app has been deleted",
        });
        if (user.creator) {
          setCreatorId(user.creator._id);
          if (!user?.creatorPartnerships.some((p) => p.status === "active")) {
            navigate({ to: PartnershipsRoute.fullPath });
          } else {
            navigate({ to: DashboardRoute.fullPath });
          }
        } else {
          setAppId(undefined);
          navigate({ to: OnboardingRoute.fullPath });
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof ConvexError
            ? error.data
            : "Failed to delete app. Please try again or contact support.",
        variant: "destructive",
      });
    }
  };

  // Update appDetails when currentApp data changes
  useEffect(() => {
    if (currentApp) {
      setAppDetails({
        description: currentApp.description ?? "",
      });
      setLogoUrl(typeof currentApp.logo === "string" ? currentApp.logo : "");
    }
  }, [currentApp]);

  return (
    <div className="flex h-full w-full flex-col gap-6">
      {/* Avatar */}
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex w-full items-start justify-between rounded-lg p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-medium text-primary">Your Avatar</h2>
            <p className="text-sm font-normal text-primary/60">
              This is your avatar. It will be displayed on your profile.
            </p>
          </div>
          <label
            htmlFor="avatar_field"
            className="group relative flex cursor-pointer overflow-hidden rounded-full transition active:scale-95"
          >
            <img
              src={user.avatarUrl}
              className="h-20 w-20 rounded-full object-cover"
              alt={user.name ?? user.email}
            />
            <div className="absolute z-10 hidden h-full w-full items-center justify-center bg-primary/40 group-hover:flex">
              <Upload className="h-6 w-6 text-secondary" />
            </div>
          </label>
          <input
            ref={fileInputRef}
            id="avatar_field"
            type="file"
            accept="image/*"
            className="peer sr-only"
            required
            tabIndex={user ? -1 : 0}
            onChange={async (event) => {
              if (!event.target.files) {
                return;
              }
              const files = Array.from(event.target.files);
              if (files.length === 0) {
                return;
              }
              startUpload(files);
            }}
          />
        </div>
        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
          <p className="text-sm font-normal text-primary/60">
            Click on the avatar to upload a custom one from your files.
          </p>
          {user.avatarUrl && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                removeUserImage({});
              }}
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* App Details */}
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 p-6">
          <h2 className="text-xl font-medium text-primary">App Details</h2>
          <p className="text-sm font-normal text-primary/60">
            Manage your app's information and links
          </p>
        </div>

        <div className="flex w-full flex-col gap-4 p-6 pt-0">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="description"
              className="text-sm font-medium text-primary"
            >
              Description
            </label>
            <textarea
              id="description"
              className="min-h-[100px] w-full rounded-md border border-border bg-transparent p-3 text-sm text-primary outline-none focus:border-primary"
              placeholder="Describe your app..."
              value={appDetails.description}
              onChange={(e) => {
                const newDetails = {
                  ...appDetails,
                  description: e.target.value,
                };
                setAppDetails(newDetails);
                debouncedUpdateApp(newDetails);
              }}
            />
          </div>

          {/* App Logo Section */}
          <div className="flex flex-col gap-4 rounded-lg border border-border bg-secondary/50 p-4">
            <div className="flex flex-col gap-2">
              <h3 className="text-lg font-medium text-primary">App Logo</h3>
              <p className="text-sm font-normal text-primary/60">
                Upload a logo file or provide a URL to your app's logo
              </p>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              {/* Logo Preview and Upload */}
              <div className="flex flex-col gap-3">
                <label
                  htmlFor="logo_field"
                  className="group relative flex cursor-pointer overflow-hidden rounded-lg border-2 border-dashed border-border transition hover:border-primary"
                >
                  {currentApp?.logo ? (
                    <div className="flex h-24 w-24 items-center justify-center">
                      {typeof currentApp.logo === "string" ? (
                        <img
                          src={currentApp.logo}
                          className="h-full w-full rounded-lg object-cover"
                          alt={`${currentApp.name} logo`}
                        />
                      ) : (
                        <img
                          src={"https://your-app-logo-url.com/logo.png"}
                          className="h-full w-full rounded-lg object-cover"
                          alt={`${currentApp.name} logo`}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center bg-secondary">
                      <Upload className="h-6 w-6 text-primary/60" />
                    </div>
                  )}
                  <div className="absolute inset-0 hidden items-center justify-center bg-primary/40 group-hover:flex">
                    <Upload className="h-6 w-6 text-secondary" />
                  </div>
                </label>
                <input
                  ref={logoFileInputRef}
                  id="logo_field"
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={async (event) => {
                    if (!event.target.files) {
                      return;
                    }
                    const files = Array.from(event.target.files);
                    if (files.length === 0) {
                      return;
                    }
                    startLogoUpload(files);
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => logoFileInputRef.current?.click()}
                >
                  Upload File
                </Button>
              </div>

              {/* URL Input */}
              <div className="flex flex-1 flex-col gap-2">
                <label
                  htmlFor="logoUrl"
                  className="text-sm font-medium text-primary"
                >
                  Or provide a URL
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    id="logoUrl"
                    className="flex-1 rounded-md border border-border bg-transparent p-3 text-sm text-primary outline-none focus:border-primary"
                    placeholder="https://example.com/logo.png"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (logoUrl.trim()) {
                        await updateApp({
                          appId,
                          logo: logoUrl.trim(),
                        });
                      }
                    }}
                  >
                    Save URL
                  </Button>
                </div>
              </div>
            </div>

            {/* Remove Logo */}
            {currentApp?.logo && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                className="w-fit"
                onClick={async () => {
                  await updateApp({
                    appId,
                    logo: "",
                  });
                  setLogoUrl("");
                }}
              >
                Remove Logo
              </Button>
            )}
          </div>
        </div>

        {/* Demo Mode Section */}
        <div className="flex w-full flex-col gap-4 border-t border-border p-6">
          <div className="flex flex-col gap-2">
            <h3 className="text-lg font-medium text-primary">Demo Mode</h3>
            <p className="text-sm font-normal text-primary/60">
              Enable a sandbox demo campaign for prospects. This is
              frontend-only and uses mock data.
            </p>
          </div>

          {/* Enable Demo Mode Toggle */}
          <div className="flex items-center justify-between rounded-md border border-border bg-secondary p-4">
            <div className="flex flex-col">
              <span className="text-sm font-medium text-primary">
                Enable Demo Mode
              </span>
              <span className="text-xs text-primary/60">
                Adds a Demo Campaign in your account switcher. Clicking it will
                render dashboards, analytics and contracts from mock data.
              </span>
            </div>
            <Button
              type="button"
              variant={demoModeEnabled ? "secondary" : "outline"}
              onClick={() => setDemoModeEnabled(!demoModeEnabled)}
            >
              {demoModeEnabled ? "On" : "Off"}
            </Button>
          </div>

          {/* Demo App Customization - Only show when demo mode is enabled */}
          {demoModeEnabled && (
            <div className="flex flex-col gap-4 rounded-md border border-border bg-secondary/50 p-4">
              <div className="flex flex-col gap-2">
                <h4 className="text-sm font-medium text-primary">
                  Customize Demo App
                </h4>
                <p className="text-xs text-primary/60">
                  Customize the app name and logo for your demo presentation.
                </p>
              </div>

              <div className="flex flex-col gap-4">
                {/* Demo App Name */}
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="demo-app-name"
                    className="text-sm font-medium text-primary"
                  >
                    App Name
                  </Label>
                  <Input
                    id="demo-app-name"
                    type="text"
                    value={demoAppName}
                    onChange={(e) => setDemoAppName(e.target.value)}
                    placeholder="Enter app name"
                    className="text-sm"
                  />
                </div>

                {/* Demo App Logo Section */}
                <div className="flex flex-col gap-4">
                  <Label className="text-sm font-medium text-primary">
                    Demo Logo
                  </Label>
                  <p className="text-xs text-primary/60">
                    Upload a logo file or provide a URL for your demo
                    presentation
                  </p>

                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                    {/* Logo Preview and Upload */}
                    <div className="flex flex-col gap-3">
                      <label
                        htmlFor="demo_logo_field"
                        className="group relative flex cursor-pointer overflow-hidden rounded-lg border-2 border-dashed border-border transition hover:border-primary"
                      >
                        <div className="flex h-20 w-20 items-center justify-center">
                          <img
                            src={demoAppLogo}
                            className="h-full w-full rounded-lg object-cover"
                            alt={`${demoAppName} demo logo`}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=200&fit=crop&crop=center";
                            }}
                          />
                        </div>
                        <div className="absolute inset-0 hidden items-center justify-center bg-primary/40 group-hover:flex">
                          <Upload className="h-4 w-4 text-secondary" />
                        </div>
                      </label>
                      <input
                        ref={demoLogoFileInputRef}
                        id="demo_logo_field"
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={async (event) => {
                          if (
                            !event.target.files ||
                            event.target.files.length === 0
                          ) {
                            return;
                          }
                          const file = event.target.files[0];
                          handleDemoLogoUpload(file);
                          // Clear the input so the same file can be selected again
                          if (demoLogoFileInputRef.current) {
                            demoLogoFileInputRef.current.value = "";
                          }
                        }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => demoLogoFileInputRef.current?.click()}
                      >
                        Upload File
                      </Button>
                    </div>

                    {/* URL Input */}
                    <div className="flex flex-1 flex-col gap-2">
                      <Label
                        htmlFor="demo-app-logo-url"
                        className="text-sm font-medium text-primary"
                      >
                        Or provide a URL
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="demo-app-logo-url"
                          type="url"
                          value={demoAppLogo}
                          onChange={(e) => setDemoAppLogo(e.target.value)}
                          placeholder="https://example.com/logo.png"
                          className="text-sm"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            // URL is already updated via onChange, just trigger a re-render
                            toast({
                              title: "Demo logo updated",
                              description: "Demo logo URL has been saved",
                            });
                          }}
                        >
                          Save URL
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="flex items-center gap-3 p-3 rounded-md border border-border bg-background">
                <img
                  src={demoAppLogo}
                  alt={demoAppName}
                  className="w-8 h-8 rounded-md object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=200&fit=crop&crop=center";
                  }}
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-primary">
                    {demoAppName}
                  </span>
                  <span className="text-xs text-primary/60">
                    Demo Campaign Preview
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Delete App Section */}
        <div className="flex w-full flex-col gap-4 border-t border-border p-6">
          <div className="flex flex-col gap-2">
            <h3 className="text-lg font-medium text-red-600">Danger Zone</h3>
            <p className="text-sm font-normal text-primary/60">
              Once you delete an app, there is no going back. Please be certain.
            </p>
          </div>

          {!showDeleteConfirmation ? (
            <Button
              type="button"
              variant="destructive"
              className="w-fit"
              onClick={() => setShowDeleteConfirmation(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete App
            </Button>
          ) : (
            <div className="flex flex-col gap-3 rounded-md border border-red-200 bg-red-50 p-4">
              <div className="flex flex-col gap-1">
                <h4 className="text-sm font-medium text-red-800">
                  Are you absolutely sure?
                </h4>
                <p className="text-sm text-red-700">
                  This action cannot be undone. This will permanently delete the
                  app "{currentApp?.name}" and all associated data including
                  creator contracts, videos, and analytics.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteApp}
                >
                  Yes, Delete App
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirmation(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
